import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription, filter } from "rxjs";
import { AppState } from "src/app/store/app.reducer";
import { selectImportedMtmaDevice, selectImportingMtmaDevice } from "src/app/store/user-file/user-file.selector";
import * as UserFileActions from 'src/app/store//user-file/user-file.actions';
import { StandardDesignParameters, StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { ExportInfo } from "src/app/model/export";
import { CustomValidators } from "src/app/validators/custom.validators";
import { MtmaAction } from "src/app/model/mtma-action";
import { selectIsImpersMode } from "src/app/store/user/user.selector";
import { TranslateService } from "@ngx-translate/core";
import { MtmaGenericEntryInfoDialogComponent } from "../../mtma-generic-entry-info-dialog/mtma-generic-entry-info.dialog.component";
import { ConfirmationService } from "primeng/api";
import { MtmaConfirmImportDialogComponent } from "../../mtma-confirm-import-dialog/mtma-confirm-import.dialog.component";

@Component({
	templateUrl: './mtma-import-dialog.component.html',
})

export class MtmaImportDeviceDialogComponent implements OnInit, OnDestroy {
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;
	isImpersMode: boolean;
	// filesForm: FormGroup;
	mtmaActionsSub: Subscription;

	mtmaActions: MtmaAction[] = [];

	exportForm: FormGroup;

	import$: Observable<boolean | undefined>;
	importSub: Subscription;

	importing: boolean;
	importing$: Observable<boolean | undefined>;
	importingSub: Subscription;

	exportInfo: ExportInfo;
	update: boolean;

	deviceTypes = [
		"MTMA_MODULES",
		"MTMA_UNITS",
		"MTMA_TONES",
	];
	usersTypes = [
		"MTMA_CONTACTS",
		"MTMA_TAGS",
		"MTMA_KEYPADS",
		"MTMA_AC_RULES",
	];
	relationsTypes = [
		"MTMA_UNITS_CONTACTS",
		"MTMA_AC_USERS",
		"MTMA_AC_BADGES",
		"MTMA_AC_KEYPADS",
	];

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// design
	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		if (config.data !== undefined && config.data.exportInfo !== undefined) {
			this.exportInfo = config.data.exportInfo;
			this.update = true;
		} else {
			this.exportInfo = new ExportInfo('', '');
			this.update = false;
		}
		this.exportInfo.DefaultAction = 255;
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();
		this.onResize();

		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.importSub?.unsubscribe();
		this.importingSub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	initSelectors(): void {
		this.import$ = this.store.select(selectImportedMtmaDevice);
		this.importing$ = this.store.select(selectImportingMtmaDevice);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	buildForm(): void {
		const deviceTypesArray : FormControl<boolean>[] = this.deviceTypes.map(() => this.fb.control(true)); // set all as true
		const usersTypesArray : FormControl<boolean>[] = this.usersTypes.map(() => this.fb.control(true)); // set all as true
		const relationsTypesArray : FormControl<boolean>[] = this.relationsTypes.map(() => this.fb.control(true)); // set all as true
		this.exportForm = new FormGroup({
			'name': new FormControl(this.exportInfo.Name, [Validators.required, Validators.minLength(5)]),
			'Action1': new FormControl(this.exportInfo.DefaultAction, Validators.required),
			'deviceTypes': this.fb.array(deviceTypesArray),
			'usersTypes': this.fb.array(usersTypesArray),
			'relationsTypes': this.fb.array(relationsTypesArray),
		});
		this.exportForm.addValidators(CustomValidators.multipleCheckboxRequireOneIO('deviceTypes', 'usersTypes'));
		this.f.name.disable();
	}

	subscribeAll(): void {
		this.importSub = this.import$?.pipe(filter(res => !!res)).subscribe(
			(res) => {
				if (res) {
					this.closeDialog();
				}
			}
		);
		this.importingSub = this.importing$?.subscribe(
			(importing) => {
				this.importing = importing;
			}
		);
		this.mtmaActionsSub = this.store.select('device', 'mtmaActions')
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions.filter(a => a.Keypads);
			});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
	}

	get f(): any {
		return this.exportForm.controls;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.exportForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.exportForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	getFieldCheckbox(type: string): string {
		switch (type) {
			case 'MTMA_MODULES':
				return 'MODULES';
			case 'MTMA_CONTACTS':
				return 'NEW_EXPORT_MTMA_CONTACTS';
			case 'MTMA_AC_RULES':
				return 'NEW_EXPORT_MTMA_GROUPS';
			case 'MTMA_AC_BADGES':
				return 'ASSOCIATE_AC_GROUP_TO_TAGS';
			case 'MTMA_AC_KEYPADS':
				return 'ASSOCIATE_AC_GROUP_TO_KEYPADS';
			case 'MTMA_AC_USERS':
				return 'ASSOCIATE_AC_GROUP_TO_CONTACTS';
			case 'MTMA_UNITS_CONTACTS':
				return 'NEW_EXPORT_MTMA_UNITS_CONTACTS';
			default:
				return ('EXPORT_' + type);
		}
	}
	
	changedBoolDropdownVoice() {
		this.infoDialogGenericEntry();
	}

	infoDialogGenericEntry() {
		// valid only for SUB_ACTION
		this.dialogService.open(MtmaGenericEntryInfoDialogComponent, {
			header: this.translate.instant('MTMA_CONF_{Information}') + ': ' + this.translate.instant('IMPORT_SUBACTION'),
			contentStyle: { overflow: 'auto' },
			data: {
				row1: 'NEW_NOTE_ACTION_IMPORT',
			}
		});
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	onCheckboxChange() {
		const deviceTypesFormArray = this.exportForm.get('deviceTypes') as FormArray;
		const usersTypesFormArray = this.exportForm.get('usersTypes') as FormArray;
		const relationsTypesFormArray = this.exportForm.get('relationsTypes') as FormArray;
		
		// deviceTypes first row indexes
		// deviceModules (0) no relations
		const deviceUnits = deviceTypesFormArray.at(1);
		// deviceDtmf (2) no relations

		// usersTypes second row indexes
		const usersContacts = usersTypesFormArray.at(0);
		const usersRfid = usersTypesFormArray.at(1);
		const usersKeypads = usersTypesFormArray.at(2);
		const usersGroupsAC = usersTypesFormArray.at(3);

		// relationsTypes third row indexes
		const relationsUnitsContacts = relationsTypesFormArray.at(0);
		const relationsGroupsACContacts = relationsTypesFormArray.at(1);
		const relationsGroupsACRfid = relationsTypesFormArray.at(2);
		const relationsGroupsACKeypads = relationsTypesFormArray.at(3);

		// Units, Contacts
		if (deviceUnits.value === false || usersContacts.value === false) {
			relationsUnitsContacts.setValue(false);
			relationsUnitsContacts.disable();
		}
		if (deviceUnits.value === true && usersContacts.value === true) {
			relationsUnitsContacts.enable();
			relationsUnitsContacts.setValue(true);
		}

		// AC Groups
		if (usersGroupsAC.value === false) {
			relationsGroupsACContacts.setValue(false);
			relationsGroupsACContacts.disable();
			relationsGroupsACRfid.setValue(false);
			relationsGroupsACRfid.disable();
			relationsGroupsACKeypads.setValue(false);
			relationsGroupsACKeypads.disable();
		} else { // usersGroupsAC is true
			// Groups Contacts
			if (usersContacts.value === true) {
				relationsGroupsACContacts.enable();
				relationsGroupsACContacts.setValue(true);
			} else {
				relationsGroupsACContacts.setValue(false);
				relationsGroupsACContacts.disable();
			}
			// Groups Rfid
			if (usersRfid.value === true) {
				relationsGroupsACRfid.enable();
				relationsGroupsACRfid.setValue(true);
			} else {
				relationsGroupsACRfid.setValue(false);
				relationsGroupsACRfid.disable();
			}
			// Groups Keypads
			if (usersKeypads.value === true) {
				relationsGroupsACKeypads.enable();
				relationsGroupsACKeypads.setValue(true);
			} else {
				relationsGroupsACKeypads.setValue(false);
				relationsGroupsACKeypads.disable();
			}
		}
	}

	save(): void {
		const childRef = this.dialogService.open(MtmaConfirmImportDialogComponent, {
			header: this.translate.instant('MTMA_CONF_{Information}') + ': ' + this.translate.instant('IMPORT_SUBACTION'),
			contentStyle: { overflow: 'auto' },
			data: {
				icon: 'cm-alert',
				acceptButtonLabel: 'IMPORT_OVERWRITE',
				acceptButtonStyle: 'p-button-warning',
				rejectButtonLabel: 'CANCEL',
				row1: 'INFO_IMPORT_DEVICE',
				row2: 'IMPORT_EXPORT_MTMA_WARNING'
			}
		});
		childRef.onClose.subscribe((accepted: boolean) => {
			if (accepted) {
				const exportTypes : string[] = [];
				const deviceTypesControls = this.exportForm.get('deviceTypes') as FormArray;
				deviceTypesControls.controls.forEach((dt,i) => {
					if (dt.value) {
						exportTypes.push(this.deviceTypes[i]);
					}
				});
				const usersTypesControls = this.exportForm.get('usersTypes') as FormArray;
				usersTypesControls.controls.forEach((ut,i) => {
					if (ut.value) {
						exportTypes.push(this.usersTypes[i]);
					}
				});
				const relationsTypesControls = this.exportForm.get('relationsTypes') as FormArray;
				relationsTypesControls.controls.forEach((rt,i) => {
					if (rt.value) {
						exportTypes.push(this.relationsTypes[i]);
					}
				});
				const importInfo = { ExportTypes: exportTypes, DefaultAction: this.f.Action1.value }
				this.store.dispatch(UserFileActions.importMtmaDevice({ exportId: this.exportInfo.Id, importInfo: importInfo, isImpersMode: this.isImpersMode }));
			}
		});
	}

	closeDialog(): void {
		this.ref.close();
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaImportDeviceDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaImportDeviceDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	manageRespStyle(classType: string): string {
		return StandardPageLayout.manageRespStyle(classType, this.screenWidth);
	}

	manageRespStylewithError(type: MtmaImportDeviceDialogComponent["f"], classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}

	showErrorRequiredCheckBox(type: MtmaImportDeviceDialogComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}

	customBorderRow(): string {
		let inputTextStyle = "";

		if (this.screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
			inputTextStyle += "row";
		}
		inputTextStyle += " border-100 border-bottom-0";
		return inputTextStyle;
	}
}